/**
 * First-party visualization registry. Each entry is a manifest (id, name, params) plus a GLSL
 * fragment body that is appended to PRELUDE. Parameters map onto uP[] in declaration order.
 */
import type { VisualizationDef } from '../../../shared/viz';

export type { VisualizationDef } from '../../../shared/viz';
import { MORE_VISUALIZATIONS } from './more';
import { FRACTAL_VISUALIZATIONS } from './fractals';
import { SPACE_VISUALIZATIONS } from './space';
import { PHYSICS_VISUALIZATIONS } from './physics';
import { LIGHT_VISUALIZATIONS } from './lights';
import { ART_VISUALIZATIONS } from './art';
import { ICON_VISUALIZATIONS } from './icons';
import { LASER_SHOW_VISUALIZATIONS } from './lasershow';
import { MELODY_VISUALIZATIONS } from './melody';

const spectrumBars: VisualizationDef = {
  id: 'spectrum-bars',
  name: 'Spectrum Bars',
  description: 'Classic log-frequency bars with glow, mirrored from the centre.',
  params: [
    { id: 'bars', label: 'Bars', type: 'float', min: 8, max: 128, default: 64, step: 1 },
    { id: 'gap', label: 'Gap', type: 'float', min: 0, max: 0.8, default: 0.25 },
    { id: 'glow', label: 'Glow', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'mirror', label: 'Mirror', type: 'float', min: 0, max: 1, default: 1, step: 1 },
    { id: 'height', label: 'Height', type: 'float', min: 0.2, max: 1.5, default: 0.85 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  float bars = floor(uP[0]);
  float x = uP[3] > 0.5 ? abs(uv.x * 2.0 - 1.0) : uv.x;
  float idx = floor(x * bars);
  float cell = fract(x * bars);
  float s = spectrum((idx + 0.5) / bars * 0.92);
  float h = s * uP[4] * (0.85 + 0.3 * pulse());
  float inBar = step(uP[1] * 0.5, cell) * step(cell, 1.0 - uP[1] * 0.5);
  float body = inBar * step(uv.y, h);
  float above = max(0.0, uv.y - h);
  float glow = inBar * exp(-above * 18.0 / max(uP[2], 0.01)) * uP[2] * 0.5;
  vec3 col = palette(idx / bars * 0.75 + uv.y * 0.25);
  vec3 c = col * (body * (0.55 + 0.45 * uv.y / max(h, 1e-3)) + glow);
  vec3 bg = palette(0.05) * 0.12 * (1.0 - uv.y) + palette(0.6) * 0.04 * pulse();
  fragColor = vec4((bg + c) * uIntensity, 1.0);
}`,
};

const radialPulse: VisualizationDef = {
  id: 'radial-pulse',
  name: 'Radial Pulse',
  description: 'A polar spectrum ring that breathes with the bass, with a waveform halo.',
  params: [
    { id: 'radius', label: 'Radius', type: 'float', min: 0.1, max: 0.5, default: 0.28 },
    { id: 'spin', label: 'Spin', type: 'float', min: -2, max: 2, default: 0.3 },
    { id: 'symmetry', label: 'Symmetry', type: 'float', min: 1, max: 8, default: 2, step: 1 },
    { id: 'halo', label: 'Waveform halo', type: 'float', min: 0, max: 1.5, default: 0.8 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / min(uResolution.x, uResolution.y);
  float r = length(p);
  float a = atan(p.y, p.x);
  float u = fract((a + uTime * uP[1] * 0.2) / TAU + 0.5);
  float us = abs(fract(u * floor(uP[2])) * 2.0 - 1.0);
  float s = spectrum(us * 0.85);
  float radius = uP[0] * (1.0 + 0.25 * uBass + 0.15 * pulse());
  float ring = radius + s * 0.14;
  float d = abs(r - ring);
  float line = exp(-d * 90.0) + 0.35 * exp(-d * 14.0);
  float inner = smoothstep(radius, 0.0, r) * (0.12 + 0.35 * pulse());
  float w = waveform(us);
  float wl = exp(-abs(r - (radius * 0.6 + w * 0.08)) * 120.0);
  vec3 col = palette(us * 0.5 + r * 0.6 + uTime * 0.02) * line
           + palette(0.9) * wl * uP[3]
           + palette(0.2) * inner;
  col *= smoothstep(1.2, 0.2, r);
  fragColor = vec4(col * uIntensity, 1.0);
}`,
};

const plasmaFlow: VisualizationDef = {
  id: 'plasma-flow',
  name: 'Plasma Flow',
  description: 'Layered sine plasma whose speed follows energy and whose waves bloom on the bass.',
  params: [
    { id: 'speed', label: 'Speed', type: 'float', min: 0, max: 3, default: 1 },
    { id: 'scale', label: 'Scale', type: 'float', min: 0.3, max: 3, default: 1 },
    { id: 'contrast', label: 'Contrast', type: 'float', min: 0.5, max: 3, default: 1.2 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution) / min(uResolution.x, uResolution.y);
  float t = uTime * uP[0] * (0.5 + uLevel * 0.5);
  vec2 q = uv * uP[1] * 4.0 * (1.0 - 0.12 * pulse());
  float v = sin(q.x + t)
          + sin(q.y * 1.3 - t * 0.7)
          + sin((q.x + q.y) * 0.7 + t * 1.3)
          + sin(length(q + vec2(sin(t * 0.3), cos(t * 0.4)) * 2.0) * 1.5 - t);
  v += uBass * 1.5 * sin(length(q) * 2.0 - t * 3.0);
  vec3 col = palette(v * 0.125 + 0.5 + uMid * 0.15);
  col *= 0.55 + 0.45 * uLevel + 0.3 * pulse();
  col = pow(col, vec3(uP[2]));
  fragColor = vec4(col * uIntensity, 1.0);
}`,
};

const tunnel: VisualizationDef = {
  id: 'tunnel',
  name: 'Beat Tunnel',
  description: 'An endless tunnel; rings travel on the beat and stripes follow the spectrum.',
  params: [
    { id: 'speed', label: 'Speed', type: 'float', min: 0, max: 3, default: 1 },
    { id: 'twist', label: 'Twist', type: 'float', min: -3, max: 3, default: 0.8 },
    { id: 'segments', label: 'Segments', type: 'float', min: 2, max: 24, default: 8, step: 1 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / min(uResolution.x, uResolution.y);
  float r = length(p) + 1e-4;
  float a = atan(p.y, p.x) / TAU;
  float depth = 0.3 / r + uTime * uP[0] * (0.6 + 0.8 * uLevel);
  float u = a + depth * uP[1] * 0.1 + uTime * 0.03;
  float stripes = 0.5 + 0.5 * sin(u * TAU * floor(uP[2]));
  float rings = 0.5 + 0.5 * sin((depth - uBeatPhase) * TAU);
  float ringPulse = pow(rings, 8.0) * (0.35 + 1.2 * pulse());
  float s = spectrum(abs(fract(u) * 2.0 - 1.0));
  vec3 col = palette(fract(depth * 0.1 + stripes * 0.2)) * (0.2 + 0.8 * stripes * s)
           + palette(0.95) * ringPulse;
  col *= smoothstep(0.0, 0.25, r);
  col *= 1.0 - 0.5 * smoothstep(0.6, 1.4, r);
  fragColor = vec4(col * uIntensity, 1.0);
}`,
};

const oscilloscope: VisualizationDef = {
  id: 'oscilloscope',
  name: 'Oscilloscope',
  description: 'A glowing waveform trace over a faint grid.',
  params: [
    { id: 'amplitude', label: 'Amplitude', type: 'float', min: 0.2, max: 2, default: 1 },
    { id: 'thickness', label: 'Thickness', type: 'float', min: 0.5, max: 6, default: 2 },
    { id: 'glow', label: 'Glow', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'grid', label: 'Grid', type: 'float', min: 0, max: 1, default: 0.6 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  float A = uP[0] * 0.42 * (1.0 + 0.25 * pulse());
  float dx = 1.0 / uResolution.x;
  float y0 = 0.5 + waveform(uv.x) * A;
  float y1 = 0.5 + waveform(uv.x + dx) * A;
  float pad = uP[1] / uResolution.y;
  float lo = min(y0, y1) - pad;
  float hi = max(y0, y1) + pad;
  float d = max(0.0, max(lo - uv.y, uv.y - hi)) * uResolution.y;
  float core = exp(-d * 0.8);
  float glow = exp(-d * 0.05) * uP[2] * 0.5;
  vec3 col = palette(uv.x * 0.7 + 0.15) * (core + glow);
  vec2 g = abs(fract(uv * vec2(16.0, 8.0)) - 0.5);
  float grid = (1.0 - smoothstep(0.0, 0.015, min(g.x, g.y))) * 0.06 * uP[3];
  col += palette(0.5) * grid + palette(0.1) * pulse() * 0.06;
  fragColor = vec4(col * uIntensity, 1.0);
}`,
};

const laserHaze: VisualizationDef = {
  id: 'laser-haze',
  name: 'Laser Haze',
  description:
    'A fan of laser beams cutting through haze. The fan sweeps and dips with the bar; a bank of thicker haze lights up where the beams pass through it.',
  params: [
    { id: 'beams', label: 'Beams', type: 'float', min: 3, max: 16, default: 9, step: 1 },
    { id: 'spread', label: 'Spread', type: 'float', min: 0.2, max: 3, default: 1.4 },
    { id: 'sweep', label: 'Sweep', type: 'float', min: 0, max: 2, default: 0.6 },
    { id: 'dip', label: 'Dip', type: 'float', min: 0, max: 1, default: 0.5 },
    {
      id: 'height',
      label: 'Projector height',
      type: 'float',
      min: -0.55,
      max: 0.5,
      default: -0.15,
    },
    { id: 'haze', label: 'Haze', type: 'float', min: 0, max: 1.5, default: 0.5 },
    { id: 'bank', label: 'Haze bank', type: 'float', min: 0, max: 2, default: 1.5 },
    { id: 'bankHeight', label: 'Bank height', type: 'float', min: -0.5, max: 0.5, default: 0.18 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  vec2 o = vec2(0.0, uP[4]);
  bool fromCeiling = uP[4] > 0.25;
  int n = int(uP[0]);
  float t = uTime * uP[2];
  float sweep = sin(t * 0.7) * 0.55 + sin(t * 0.29 + 1.3) * 0.35;
  // Dip: the fan opens past horizontal over each bar, so the outer beams swing down into the crowd.
  float dip = uP[3] * (0.5 - 0.5 * cos(uBarPhase * TAU)) * 1.3;
  vec3 beams = vec3(0.0);
  for (int i = 0; i < 16; i++) {
    if (i >= n) break;
    float fi = float(i) / max(float(n - 1), 1.0);
    float side = fi - 0.5;
    float ang = side * uP[1] * (1.0 + dip) + sweep * (0.5 + 0.5 * uMid) * (1.0 - 0.5 * uP[3]);
    vec2 d = vec2(sin(ang), cos(ang));
    if (fromCeiling) d.y = -d.y;
    float energy = 0.3 + 0.9 * uBands[i % 8] + 0.5 * pulse();
    beams += palette(0.1 + fi * 0.8) * beam(p, o, d, 0.0024 * (1.0 + 0.3 * uBass)) * energy;
  }
  // Haze everywhere, plus a denser bank the beams light up as they cut through it.
  float bankY = (p.y - uP[7]) / 0.13;
  float bank = uP[6] * exp(-bankY * bankY) * (0.6 + 0.8 * fbm(p * vec2(2.5, 6.0) + vec2(uTime * 0.05, 0.0)));
  float haze = hazeAt(p, uP[5]) * (0.6 + 4.0 * bank);
  vec3 col = beams * haze;
  // The bank glows where beams cross it (light scattered sideways in the smoke).
  col += beams * bank * 0.6 + palette(0.4) * bank * 0.04;
  col += palette(0.5) * exp(-length(p - o) * 7.0) * (0.4 + 0.6 * uLevel);
  col += palette(0.15) * (0.02 + 0.05 * bank) * (uLevel + pulse()) * haze;
  fragColor = vec4(col * uIntensity, 1.0);
}`,
};

/** A drifting bank of haze/smoke, lit by the beat: layer it over lasers or anything else. */
const hazeLayer: VisualizationDef = {
  id: 'haze',
  name: 'Haze',
  description: 'Drifting stage haze, lit softly by the music. Put it on top of other layers.',
  tags: ['energy:low', 'style:ambient'],
  role: 'element',
  params: [
    { id: 'density', label: 'Density', type: 'float', min: 0, max: 2, default: 0.8 },
    { id: 'height', label: 'Height', type: 'float', min: -0.5, max: 0.6, default: -0.1 },
    { id: 'depth', label: 'Thickness', type: 'float', min: 0.05, max: 1.5, default: 0.4 },
    { id: 'drift', label: 'Drift', type: 'float', min: 0, max: 2, default: 0.6 },
    { id: 'glow', label: 'Beat glow', type: 'float', min: 0, max: 2, default: 0.7 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float y = (p.y - uP[1]) / uP[2];
  float bank = exp(-y * y) + 0.25 * smoothstep(0.0, -1.5, y);
  vec2 q = p * vec2(2.0, 4.0) + vec2(uTime * 0.04 * uP[3], 0.0);
  float n = fbm(q + fbm(q * 1.7 - uTime * 0.02 * uP[3]) * 1.5);
  float d = clamp(bank * (0.25 + n) * uP[0], 0.0, 1.5);
  vec3 col = mix(vec3(0.6), palette(0.5 + n * 0.3), 0.35) * d * (0.32 + 0.3 * uLevel + uP[4] * 0.4 * pulse());
  fragColor = vec4(col, 1.0);
}`,
};

const laserCone: VisualizationDef = {
  id: 'laser-cone',
  name: 'Laser Cone',
  description:
    'Looking down the barrel of a laser cone: radiating beams and a pulsing light sheet.',
  params: [
    { id: 'beams', label: 'Beams', type: 'float', min: 6, max: 48, default: 24, step: 1 },
    { id: 'rotation', label: 'Rotation', type: 'float', min: -2, max: 2, default: 0.4 },
    { id: 'cone', label: 'Cone size', type: 'float', min: 0.1, max: 0.6, default: 0.32 },
    { id: 'haze', label: 'Haze', type: 'float', min: 0, max: 1.5, default: 1 },
    { id: 'twist', label: 'Twist', type: 'float', min: -2, max: 2, default: 0.4 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float r = length(p);
  float a = atan(p.y, p.x);
  float n = floor(uP[0]);
  float aa = a + uTime * uP[1] * 0.3 + uP[4] * r * 1.5;
  float cell = aa / TAU * n;
  float idx = floor(cell);
  float arc = abs(fract(cell) - 0.5) * TAU / n * r;
  float w = 0.0015 + 0.004 * r;
  float b = exp(-arc * arc / (w * w)) + 0.2 * exp(-arc / (w * 6.0));
  float band = uBands[int(mod(idx, 8.0))];
  float cone = uP[2] * (1.0 + 0.12 * pulse() + 0.08 * uBass);
  float along = smoothstep(0.0, 0.04, r) * exp(-max(0.0, r - cone) * 2.2);
  float sheet = exp(-pow((r - cone) / 0.01, 2.0)) * (0.35 + 1.1 * pulse()) + 0.12 * exp(-abs(r - cone) * 28.0);
  float haze = hazeAt(p, uP[3]);
  vec3 col = palette(fract(idx / n)) * b * along * (0.3 + 1.0 * band) * haze;
  col += palette(0.9) * sheet * haze;
  col += palette(0.6) * exp(-r * 20.0) * (0.5 + uLevel);
  fragColor = vec4(col * uIntensity, 1.0);
}`,
};

const edgeLasers: VisualizationDef = {
  id: 'edge-lasers',
  name: 'Edge Lasers',
  description:
    'Emitters around the screen edges fire crossing beams; each switches on with its band.',
  params: [
    { id: 'emitters', label: 'Per edge', type: 'float', min: 1, max: 4, default: 2, step: 1 },
    { id: 'sweep', label: 'Sweep', type: 'float', min: 0, max: 2, default: 0.7 },
    { id: 'width', label: 'Beam width', type: 'float', min: 0.5, max: 3, default: 1 },
    { id: 'haze', label: 'Haze', type: 'float', min: 0, max: 1.5, default: 0.9 },
    { id: 'threshold', label: 'Threshold', type: 'float', min: 0, max: 0.8, default: 0.2 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float hx = 0.5 * uResolution.x / uResolution.y + 0.02;
  int k = int(uP[0]);
  vec3 col = vec3(0.0);
  for (int e = 0; e < 4; e++) {
    for (int j = 0; j < 4; j++) {
      if (j >= k) break;
      float fj = (float(j) + 0.5) / float(k);
      vec2 o;
      float base;
      if (e == 0)      { o = vec2(-hx, mix(-0.38, 0.38, fj));      base = 0.0; }
      else if (e == 1) { o = vec2(hx, mix(0.38, -0.38, fj));       base = PI; }
      else if (e == 2) { o = vec2(mix(-0.8, 0.8, fj) * hx, 0.52);  base = -0.5 * PI; }
      else             { o = vec2(mix(0.8, -0.8, fj) * hx, -0.52); base = 0.5 * PI; }
      float id = float(e * 4 + j);
      float ang = base + sin(uTime * uP[1] * (0.5 + 0.07 * id) + id * 1.7) * 0.65;
      vec2 d = vec2(cos(ang), sin(ang));
      float band = uBands[(e * 2 + j) % 8];
      float on = smoothstep(uP[4], uP[4] + 0.3, band + 0.35 * pulse());
      col += palette(0.15 + id / 18.0) * beam(p, o, d, 0.0022 * uP[2]) * (0.25 + 1.4 * on);
    }
  }
  col *= hazeAt(p, uP[3]);
  fragColor = vec4(col * uIntensity, 1.0);
}`,
};

/** Visualizations that draw shapes on black and work best over a background (see VizRole). */
const ELEMENTS = new Set([
  'spectrum-bars',
  'radial-pulse',
  'oscilloscope',
  'edge-lasers',
  'polar-bars',
  'particle-swarm',
  'led-equalizer',
]);

export const VISUALIZATIONS: readonly VisualizationDef[] = [
  spectrumBars,
  radialPulse,
  plasmaFlow,
  tunnel,
  oscilloscope,
  laserHaze,
  hazeLayer,
  laserCone,
  edgeLasers,
  ...MORE_VISUALIZATIONS,
  ...FRACTAL_VISUALIZATIONS,
  ...SPACE_VISUALIZATIONS,
  ...PHYSICS_VISUALIZATIONS,
  ...LIGHT_VISUALIZATIONS,
  ...ART_VISUALIZATIONS,
  ...ICON_VISUALIZATIONS,
  ...LASER_SHOW_VISUALIZATIONS,
  ...MELODY_VISUALIZATIONS,
].map((v) => (v.role ? v : { ...v, role: ELEMENTS.has(v.id) ? 'element' : 'background' }));

export function findVisualization(id: string): VisualizationDef {
  return VISUALIZATIONS.find((v) => v.id === id) ?? VISUALIZATIONS[0]!;
}
